import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  getAllUsers,
  getAllGameStories,
  toggleUserActive,
} from "../../utils/api";
import LoadingState from "../../components/LoadingState";
import axios from "axios";

const Dashboard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState({
    activeUsers: 0,
    totalUsers: 0,
    activeStories: 0,
    totalStories: 0,
  });
  const [users, setUsers] = useState([]);
  const [stories, setStories] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const loadData = async () => {
      try {
        const storyData = await fetchDashboardData();
        if (storyData && storyData.length > 0) {
          await fetchUsers();
        } else {
          const storiesResponse = await getAllGameStories();
          if (storiesResponse && storiesResponse.success && storiesResponse.data.length > 0) {
            setStories(storiesResponse.data);
            await fetchUsers();
          } else {
            await fetchUsers();
          }
        }
      } catch (error) {
        console.error("Error loading data:", error);
        setError("データの読み込み中にエラーが発生しました");
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchDashboardData = async () => {
    setIsLoading(true);
    setError("");

    try {
      const storiesResponse = await getAllGameStories();

      if (storiesResponse && storiesResponse.success) {
        const activeStoriesCount = storiesResponse.data.filter(
          (story) => story.isActive
        ).length;

        setStories(storiesResponse.data);

        setStats({
          activeStories: activeStoriesCount,
          totalStories: storiesResponse.data.length,
          activeUsers: 0,
          totalUsers: 0,
        });

        return storiesResponse.data;
      }
    } catch (err) {
      setError(`Error fetching dashboard data: ${err.message}`);
    }
    return [];
  };

  const fetchUsers = async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await getAllUsers();

      if (response && response.success) {
        setUsers(response.data);

        const activeUsersCount = response.data.filter(
          (user) => user.isActive
        ).length;
        setStats((prevStats) => ({
          ...prevStats,
          activeUsers: activeUsersCount,
          totalUsers: response.data.length,
        }));
      } else {
        setError("Failed to load users");
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const findStoryById = (storyId) => {
    if (!storyId || !stories || stories.length === 0) return null;

    let match = stories.find((s) => s.storyId === storyId);
    if (match) return match;

    match = stories.find(
      (s) =>
        s.storyId &&
        storyId &&
        s.storyId.toString().toLowerCase() === storyId.toString().toLowerCase()
    );
    if (match) return match;

    if (storyId.includes("-")) {
      const idPart = storyId.split("-").pop();
      match = stories.find((s) => s.storyId && s.storyId.endsWith(idPart));
      if (match) return match;
    }

    match = stories.find(
      (s) =>
        s.storyId &&
        storyId &&
        (s.storyId.startsWith(storyId) || storyId.startsWith(s.storyId))
    );
    if (match) return match;

    if (stories.length > 0) return stories[0];

    return null;
  };

  const getStoryName = (storyId) => {
    if (!storyId) return "ストーリーなし";
    const story = findStoryById(storyId);
    return story ? story.title : `ストーリーID: ${storyId}`;
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      setError("");
      const response = await toggleUserActive(userId);

      if (response && response.success) {
        setSuccessMessage(
          `ユーザーが${
            response.data.isActive ? "アクティブ化" : "停止"
          }されました`
        );

        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user._id === userId ? { ...user, isActive: !currentStatus } : user
          )
        );

        fetchUsers();
      } else {
        setError(response.message || "ユーザーステータスの更新に失敗しました");
      }
    } catch (err) {
      setError(`エラー: ${err.message}`);
    }
  };

  const getFilteredUsers = () => {
    if (filter === "all") return users;
    if (filter === "active") return users.filter((user) => user.isActive);
    if (filter === "pending") return users.filter((user) => !user.isActive);
    return users;
  };

  const updateStoryTitlesToJapanese = async () => {
    try {
      setError("");
      setIsLoading(true);

      const apiUrl =
        process.env.REACT_APP_API_URL || "http://localhost:5001/api";
      const adminToken = localStorage.getItem("adminAuth");

      if (!adminToken) {
        throw new Error("管理者認証が必要です");
      }

      const response = await axios.post(
        `${apiUrl}/stories/update-titles`,
        {},
        {
          headers: {
            Authorization: `Bearer ${adminToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data && response.data.success) {
        setSuccessMessage("ストーリータイトルが日本語に更新されました");
        fetchDashboardData();
        fetchUsers();
      } else {
        throw new Error(response.data.message || "タイトル更新に失敗しました");
      }
    } catch (err) {
      console.error("Error updating story titles:", err);
      setError(`ストーリータイトルの更新エラー: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && !error) {
    return <LoadingState message="データを読み込み中..." />;
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">管理者ダッシュボード</h1>
        <p className="text-gray-600 mt-1">ゲーム統計の概要</p>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="container mx-auto px-4 py-8">
          {error && (
            <div className="bg-red-100 text-red-700 p-4 rounded mb-6">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="bg-green-100 text-green-700 p-3 rounded mb-4">
              {successMessage}
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="text-gray-500 text-sm">アクティブユーザー</div>
              <div className="text-3xl font-bold mt-2">{stats.activeUsers}</div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="text-gray-500 text-sm">合計ユーザー</div>
              <div className="text-3xl font-bold mt-2">{stats.totalUsers}</div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="text-gray-500 text-sm">アクティブストーリー</div>
              <div className="text-3xl font-bold mt-2">{stats.activeStories}</div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="text-gray-500 text-sm">合計ストーリー</div>
              <div className="text-3xl font-bold mt-2">{stats.totalStories}</div>
            </div>
          </div>

          {/* Management Modules */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="font-bold text-xl mb-2">ユーザー管理</h2>
              <p className="text-gray-600 mb-4">ユーザーの承認と状態管理</p>
              <Link
                to="/admin/users"
                className="inline-block bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                ユーザー管理へ
              </Link>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="font-bold text-xl mb-2">ストーリー管理</h2>
              <p className="text-gray-600 mb-4">ストーリーの作成・編集・削除</p>
              <div className="flex space-x-2">
                <Link
                  to="/admin/stories"
                  className="inline-block bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  ストーリー管理へ
                </Link>
                <button
                  onClick={updateStoryTitlesToJapanese}
                  disabled={isLoading}
                  className="inline-block bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600"
                >
                  {isLoading ? "処理中..." : "タイトルを日本語化"}
                </button>
              </div>
            </div>
          </div>

          {/* User List */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-bold mb-4">最近のユーザー登録</h2>
            <div className="flex space-x-2 mb-4">
              <button
                onClick={() => setFilter("all")}
                className={`px-3 py-1 rounded text-sm ${
                  filter === "all" ? "bg-blue-500 text-white" : "bg-gray-200"
                }`}
              >
                全て
              </button>
              <button
                onClick={() => setFilter("active")}
                className={`px-3 py-1 rounded text-sm ${
                  filter === "active" ? "bg-green-500 text-white" : "bg-gray-200"
                }`}
              >
                アクティブ
              </button>
              <button
                onClick={() => setFilter("pending")}
                className={`px-3 py-1 rounded text-sm ${
                  filter === "pending"
                    ? "bg-yellow-500 text-white"
                    : "bg-gray-200"
                }`}
              >
                承認待ち
              </button>
              <button
                onClick={fetchUsers}
                className="px-3 py-1 rounded text-sm bg-gray-200 hover:bg-gray-300"
              >
                更新
              </button>
            </div>

            {isLoading ? (
              <div className="text-center py-4">ローディング中...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ユーザー名
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        部屋番号
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ストーリー
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ステータス
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        登録日時
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        アクション
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getFilteredUsers().length > 0 ? (
                      getFilteredUsers().map((user) => (
                        <tr key={user._id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {user.guestName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {user.roomNumber}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStoryName(user.storyId)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 py-1 rounded-full text-xs ${
                                user.isActive
                                  ? "bg-green-100 text-green-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {user.isActive ? "アクティブ" : "承認待ち"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {new Date(user.createdAt).toLocaleString("ja-JP")}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() =>
                                handleToggleActive(user._id, user.isActive)
                              }
                              className={`px-3 py-1 rounded text-xs ${
                                user.isActive
                                  ? "bg-red-100 text-red-800 hover:bg-red-200"
                                  : "bg-green-100 text-green-800 hover:bg-green-200"
                              }`}
                            >
                              {user.isActive ? "停止" : "承認"}
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="6"
                          className="px-6 py-4 text-center text-gray-500"
                        >
                          ユーザーが見つかりません
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;